import {
  Divider,
  Heading,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
  useBoolean,
} from "@chakra-ui/react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Trans, t } from "@lingui/macro";
import { FormRow, type IOption, Select } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import {
  DEFAULT_PLANNING_SETTING,
  getSavedPlanningViewSettings,
  MY_SETTINGS_PROPERTIES,
  PLANNING_SETTING_PROPERTIES,
  parsePlanningViewSettings,
} from "@src/stores/models/WorkspaceSettings";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { SortableSettingItem } from "@src/widgets/PlanningSettingsPopover/SortableSettingItem";
import { FieldState } from "formstate";
import { isEmpty } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

type UserBooleanSetting = "default" | "true" | "false";

class Form {
  show_task = new FieldState<UserBooleanSetting>("default");
  show_project = new FieldState<UserBooleanSetting>("default");
  show_title = new FieldState<UserBooleanSetting>("default");
  show_budget_item = new FieldState<UserBooleanSetting>("default");
  show_note = new FieldState<UserBooleanSetting>("default");
  adjust_allocation_height_by_duration = new FieldState<UserBooleanSetting>(
    "default",
  );
  text_order = new FieldState([...DEFAULT_PLANNING_SETTING.text_order]);
}

export const PlanningSettingsPopover = observer(
  function PlanningSettingsPopover() {
    const BooleanOptions: IOption[] = [
      { label: t`Default`, value: "default" },
      { label: t`Yes`, value: "true" },
      { label: t`No`, value: "false" },
    ];
    const SORTABLE_SETTINGS = [
      {
        label: t`Show project`,
        id: PLANNING_SETTING_PROPERTIES.SHOW_PROJECT as const,
      },
      {
        label: t`Show title`,
        id: PLANNING_SETTING_PROPERTIES["SHOW_TITLE"] as const,
      },
      {
        label: t`Show task`,
        id: PLANNING_SETTING_PROPERTIES.SHOW_TASK as const,
      },
      {
        label: t`Show budget item`,
        id: PLANNING_SETTING_PROPERTIES.SHOW_BUDGET_ITEM as const,
      },
    ];

    const { workspaceStore, UIStore } = useStore();
    const [form] = useState(() => new Form());
    const [sortableSettings, setSortableSettings] = useState(
      SORTABLE_SETTINGS.sort(
        (a, b) =>
          form.text_order.$.indexOf(a.id) - form.text_order.$.indexOf(b.id),
      ),
    );
    const [isSubmitting, setIsSubmitting] = useBoolean(false);

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !active || active.id === over.id) return;

      setSortableSettings((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);

        const newValue = arrayMove(items, oldIndex, newIndex);
        form.text_order.onChange(newValue.map((setting) => setting.id));

        return newValue;
      });
      setTimeout(() => onSubmit(), 300);
    };

    const onSubmit = async () => {
      setIsSubmitting.on();
      const planning_view_settings = Object.values(MY_SETTINGS_PROPERTIES).map(
        (key) => ({
          name: key,
          value: form[key].$.toString(),
        }),
      );

      localStorage.setItem(
        "planning_view_settings",
        JSON.stringify({
          planning_view_settings,
        }),
      );

      workspaceStore.settings!.users_planning_view_settings =
        getSavedPlanningViewSettings(
          workspaceStore.settings!.default_planning_view_settings,
        );

      setIsSubmitting.off();

      UIStore.toast({
        title: t`Settings saved`,
        status: "success",
      });
    };

    useEffect(() => {
      const savedSettings = JSON.parse(
        localStorage.getItem("planning_view_settings") ?? "{}",
      );

      if (isEmpty(savedSettings)) return;

      Object.values(MY_SETTINGS_PROPERTIES).forEach((key) => {
        const value = parsePlanningViewSettings(
          savedSettings?.planning_view_settings,
        )[key];
        if (value === undefined) return;
        // @ts-expect-error
        form[key].onChange(value.toString());
      });
    }, []);

    return (
      <Popover isLazy>
        <PopoverTrigger>
          <IconButton
            aria-label={t`Planning settings`}
            colorScheme="grey"
            icon={<Icon w="5" h="5" name="settings-01" />}
            variant="ghost"
          />
        </PopoverTrigger>
        <PopoverContent w="400px">
          <PopoverArrow />
          <PopoverCloseButton top="2.5" />
          <PopoverHeader p="3">
            <HStack>
              <Heading size="sm">
                <Trans>Settings</Trans>
              </Heading>
              {isSubmitting && (
                <HStack ml="6">
                  <Spinner color="grey.400" size="xs" />
                  <Text color="grey.500" fontSize="sm">
                    <Trans>Saving...</Trans>
                  </Text>
                </HStack>
              )}
            </HStack>
          </PopoverHeader>
          <PopoverBody>
            <Stack dir="vertical" spacing="3">
              <FormRow
                title={t`Adjust allocation height by duration`}
                titleContainerStyle={{ flex: 1 }}
                formContainerStyle={{ maxW: 150 }}
              >
                <Select
                  {...fieldToSelectProps(
                    form.adjust_allocation_height_by_duration,
                    BooleanOptions,
                    (val) => val,
                    onSubmit,
                  )}
                />
              </FormRow>

              <Divider />

              <Stack id="sortable-my-container-for-dnd-restriction">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event)}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  <SortableContext
                    items={sortableSettings}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortableSettings.map((setting) => (
                      <SortableSettingItem
                        id={setting.id}
                        key={setting.id}
                        pb="3"
                      >
                        <FormRow
                          title={setting.label}
                          titleContainerStyle={{ flex: 1 }}
                          w="full"
                        >
                          <Select
                            {...fieldToSelectProps(
                              form[setting.id],
                              BooleanOptions,
                              (val) => val,
                              onSubmit,
                            )}
                          />
                        </FormRow>
                      </SortableSettingItem>
                    ))}
                  </SortableContext>
                </DndContext>
              </Stack>

              <FormRow
                pl="10"
                title={t`Show note`}
                titleContainerStyle={{ flex: 1 }}
                formContainerStyle={{ maxW: 150 }}
              >
                <Select
                  {...fieldToSelectProps(
                    form.show_note,
                    BooleanOptions,
                    (val) => val,
                    onSubmit,
                  )}
                />
              </FormRow>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  },
);
